import { FC, useState, useEffect, MouseEvent, useMemo, Fragment } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import Icon from 'components/icon/Icon'
import LanguageSwitcher from 'components/language-switcher/LanguageSwitcher'
import Button from 'components/button/Button'
import { routes } from 'utils/routes'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import { setMarketingParamsInStore } from 'utils/customerFlow'
import { HeaderProps as Props } from './types'
import { HEADER_BREAK_POINT } from './constants'
import styles from './Header.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from 'store/types'
import { SimulationType } from 'store/marketing/types'
import { getNavigationItems } from 'components/header/navigationItems'

const Header: FC<Props> = ({ isHome, children }) => {
  // REDUX STORE
  const marketing = useSelector((store: Store) => store.marketing)
  const { didSimulation } = useSelector((store: Store) => store.simulation)
  const { selectedProducer } = useSelector((store: Store) => store.app)
  const dispatch = useDispatch()

  // ROUTER
  const router = useRouter()
  const { locale: language, route } = router
  const isProducerDetailPage = route === '/producers/[slug]'
  const isProducerOverviewPage = route === '/producers'
  const isMiniGeneratorsPage = route === '/mini-generators'

  // i18n
  const { t } = useTranslation('header')

  // STATE
  const [menuOpen, setMenuOpen] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)
  const [openDropdownItems, setOpenDropdownItems] = useState<string[]>([])

  // NAVIGATION ITEMS
  const navigationItems = useMemo(() => getNavigationItems({ language: language || 'nl', t }), [language])

  /**
   * Triggered on first render
   */
  useEffect(() => {
    /**
     * Handles the scroll event
     */
    const handleScroll = () => setIsScrolling(window.scrollY > 0)

    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  /**
   * Prevents scrolling when mobile menu is open
   */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'visible'

    if (menuOpen) {
      // set dropdowns open when menu is open
      setOpenDropdownItems(navigationItems.filter((item) => !!item.subItems?.length).map((item) => item.text))
    }
  }, [menuOpen])

  /**
   * Handles the hamburger menu button click event
   */
  const handleHamburgerClick = () => setMenuOpen(!menuOpen)

  /**
   * Handles the simulation button click
   */
  const handleSimulationButtonClick = () => {
    setMenuOpen(false)

    if (isMiniGeneratorsPage) {
      const marketingParams = {
        sourceType: 'Pull',
        simulationSo: 'Mini-opwekkers'
      }
      setMarketingParamsInStore(marketingParams)
    } else if (!didSimulation) {
      dispatch(showSimulation())
      return
    } else if (isProducerDetailPage) {
      const { query } = router
      const { slug } = query

      return router.push(routes(language, slug as string).registration)
    } else {
      if (isProducerOverviewPage) {
        return router.push(routes(language).personalizedOffer)
      } else if (selectedProducer) {
        return router.push(routes(language, selectedProducer?.slug).registration)
      } else {
        return router.push(routes(language).personalizedOffer)
      }
    }
  }

  /**
   * Function to stop events
   */
  const stopEvents = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation() // to make sure mobile nav doesn't close on click (because of general click handler on ul element)
  }

  /**
   * Returns if viewport has desktop size or not
   */
  const isDesktop = () => {
    // This also runs on the server so we need to check if window is not undefined
    if (typeof window !== 'undefined') {
      return window.innerWidth > HEADER_BREAK_POINT
    }

    return false
  }

  /**
   * Checks if the current pathname is active or not
   *
   * @param {string[]} routes
   * @returns boolean
   */
  const checkCurrentPathname = (routes: string[]): boolean => {
    if (!routes.length) return false
    return routes.length === 1
      ? router.pathname.includes(routes[0])
      : routes.reduce((rv: boolean, curr: string) => (!rv ? router.pathname.includes(curr) : rv), false)
  }

  /**
   * Returns the header links
   */
  const getNavigationLinks = () => (
    <ul onClick={() => setMenuOpen(false)} role="presentation">
      {navigationItems.map((item, idx) => {
        // check if navigation item is a dropdown or not
        const hasSubItems = !!item.subItems?.length

        return !item.showOnLanguage || item.showOnLanguage === language ? (
          <li
            key={`${item.text}-${idx}`}
            className={styles['dropdown-trigger']}
            onMouseEnter={() => {
              hasSubItems && isDesktop() && setOpenDropdownItems([item.text])
            }}
            onMouseLeave={() => {
              hasSubItems && isDesktop() && setOpenDropdownItems([])
            }}
          >
            <Link href={item.destination}>
              <a
                className={classNames({
                  [styles.open]: openDropdownItems.includes(item.text),
                  [styles['is-active-page']]: checkCurrentPathname(item.isActiveRoutes)
                })}
                onClick={(e) => {
                  hasSubItems && stopEvents(e)
                  hasSubItems &&
                    !isDesktop() &&
                    setOpenDropdownItems(
                      !openDropdownItems.includes(item.text)
                        ? [...openDropdownItems, item.text]
                        : openDropdownItems.filter((dropdownItem) => dropdownItem !== item.text)
                    )
                }}
                role="presentation"
              >
                {item.text}
                {hasSubItems && <Icon name="dropdown" color="currentColor" />}
              </a>
            </Link>
            {hasSubItems && (
              <div
                className={classNames(styles.dropdown, {
                  [styles.show]: isDesktop() && openDropdownItems.includes(item.text),
                  [styles['show-mobile']]: !isDesktop() && openDropdownItems.includes(item.text)
                })}
              >
                <ul>
                  {item.subItems.map((subItem, subIdx) => (
                    <li key={`${subItem.text}-${subIdx}`}>
                      <Link href={subItem.destination}>
                        <a
                          onClick={() => isDesktop() && setOpenDropdownItems([])}
                          onKeyPress={() => isDesktop() && setOpenDropdownItems([])}
                          role="presentation"
                        >
                          {subItem.text}
                        </a>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ) : (
          <Fragment key={`${item.text}-${idx}`} />
        )
      })}
    </ul>
  )

  return (
    <header
      className={classNames([styles.header], {
        [styles['is-home']]: isHome,
        [styles['with-menu-open']]: menuOpen,
        [styles['is-scrolling']]: isScrolling || marketing.banner || marketing.bannerType !== ''
      })}
    >
      <div className={classNames('container', styles.container)}>
        <div className={styles.menu}>
          {children}
          <div className={styles['logo-container']}>
            <Link href={routes(language).home}>
              <a className={styles.logo}>
                <svg viewBox="0 0 130 36" className={styles.image}>
                  <g fill="none" fillRule="evenodd">
                    {/* Text */}
                    <g fill="currentColor" fillRule="nonzero">
                      <path d="M72.21 22.2c0 4.49-3.06 6.8-9 6.8H48.06V4.52h14.65c5.72 0 8.67 2.23 8.67 6.59a5.18 5.18 0 0 1-3.78 5.33c3.06.82 4.61 2.73 4.61 5.76zm-18.5-7.92h8.93c2.16 0 3.2-.8 3.2-2.42 0-1.62-1-2.52-3.13-2.52h-9v4.94zm12.78 7.34c0-1.69-1.12-2.52-3.35-2.52h-9.47v5.08h9.54c2.16 0 3.24-.83 3.24-2.56h.04zM76.53 19.89c0-5.72 4.18-9.54 10.44-9.54s10.44 3.82 10.44 9.54c0 5.72-4.18 9.54-10.44 9.54s-10.44-3.82-10.44-9.54zm15.37 0c0-3.09-1.91-5.07-4.93-5.07-3.02 0-4.93 2-4.93 5.07 0 3.07 1.91 5.08 4.93 5.08 3.02 0 4.93-1.98 4.93-5.08zM102.59 3.44h5.4V29h-5.4zM129.12 28.31a11.53 11.53 0 0 1-5.29 1.12c-4.68 0-6.94-1.87-6.94-5.72v-8.82h-4.4v-4.11h4.4V4.52h5.4v6.26h6.37v4.11h-6.37v7.7c0 1.73.75 2.42 2.62 2.42a7.2 7.2 0 0 0 3.28-.79l.93 4.09z" />
                    </g>
                    {/* Icon */}
                    <g className={styles.icon}>
                      <path
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18 26.7V18h16v8.7l-4.02 3.65L26 34l-4.02-3.65zM18 9.3V18H2V9.3l4.02-3.65L10 2l4.02 3.65z"
                      />
                    </g>
                  </g>
                </svg>
              </a>
            </Link>
            <div className={styles['language-switcher-mobile']}>
              <LanguageSwitcher />
            </div>
          </div>
          <button
            className={styles['hamburger-container']}
            type="button"
            aria-label="Menu"
            aria-controls="navigation"
            onClick={() => handleHamburgerClick()}
          >
            {!menuOpen && <div className={styles['hamburger-label']}>Menu</div>}
            <div className={classNames(styles.hamburger, { [styles.active]: menuOpen })}>
              <span className={styles.bar} />
              <span className={styles.bar} />
              <span className={styles.bar} />
            </div>
          </button>
          <nav className={styles.nav}>{getNavigationLinks()}</nav>
        </div>
        <nav className={styles['mobile-nav']}>{getNavigationLinks()}</nav>
        <div className={styles['language-switcher']}>
          <LanguageSwitcher />
        </div>
        <div className={styles.cta}>
          <Button onClick={() => handleSimulationButtonClick()} withGradient isFullWidthMobile>
            {didSimulation || marketing.simulationType !== SimulationType.PRICING_SIMULATION ? (
              <>
                {isProducerOverviewPage && t('personalizedOffer', 'Gepersonaliseerd aanbod')}
                {!selectedProducer && !isProducerOverviewPage && t('pickProducer', 'Kies jouw opwekker')}
                {selectedProducer && !isProducerOverviewPage && t('subscribeNow', 'Ik registreer me')}
              </>
            ) : (
              t('startTransfer', 'Word afnemer')
            )}
          </Button>
          <a href={`https://my.boltenergie.be/login?lang=${language}`} target="_blank" rel="noreferrer" className={styles.portalButton}>
            <Icon name="portal" color="currentColor" />
          </a>
        </div>
      </div>
    </header>
  )
}

Header.defaultProps = {
  isHome: false
}

export default Header
